/**
 * PRICES — the one seam between this app and what anything costs.
 *
 * Specified by `docs/specs/thinlms-course-pathway-pricing_spec.md`. Every rule below cites
 * the requirement it implements, because most of them look like fussy detail until the day
 * a shop gives its stock away.
 *
 * ---------------------------------------------------------------------------------
 * THREE THINGS THIS FILE EXISTS TO GET RIGHT.
 *
 * 1. A MISSING PRICE IS NEVER "FREE" (R6.0). The load-bearing distinction is between
 *    *absent from a payload that loaded* — which is the payload's own statement that this
 *    item is not priced, so it is free — and *no payload at all, a malformed one, a mode
 *    this build does not know, or a null amount* — which is `unknown`, renders as
 *    "Price unavailable", and disables every buy control on the page. The two look
 *    identical from inside a component and they are opposite facts, so no component is
 *    allowed to decide: `readPricingSeam` decides once, at the seam, and hands out
 *    `PriceView`s that have already been narrowed.
 *
 * 2. MONEY IS FORMATTED IN EXACTLY ONE PLACE (R5.1, AC-029). `formatMoney` below is the
 *    only `Intl.NumberFormat` in `apps/web/src`, for the same reason `formatDate` lives
 *    once in `catalogue.ts` and `pct` lives once in `paths.ts`: a second formatter is a
 *    second rounding rule, and two rounding rules on one product is a price that changes
 *    depending on which page you read it from.
 *
 * 3. NO FLOAT EVER TOUCHES AN AMOUNT (NFR-U6). Amounts, fees and discounts are integers in
 *    the currency's minor unit from the wire to the last division inside `formatMoney`.
 *    `0.15 × 12900` is not exactly representable and would round two different ways on two
 *    different pages; basis points with `Math.ceil`/`Math.floor` cannot.
 *
 * 4. A REPAIRABLE PRICE IS NOT A BROKEN ONE, and conflating them deadlocked the whole
 *    feature. `price_synced: false` used to set `buyable: false`, per AC-003b. But the
 *    payments app's self-heal — `catalog.resolve_for_checkout`, which recreates a missing or
 *    stale Stripe Product and Price and logs `OrderEvent.CATALOG_REPAIRED` — runs at
 *    CHECKOUT, and that rule was what stopped a learner ever reaching checkout. The
 *    mechanism that would have fixed the problem sat behind a door only it could open, so on
 *    dev every priced course was permanently unavailable. An unsynced price whose server-side
 *    checks all passed is now buyable and carries `syncPending`; a price the server refused
 *    is still refused, before the click, with the server's own sentence. See `PriceView
 *    .buyable`.
 *
 * ---------------------------------------------------------------------------------
 * WHAT THIS FILE DOES NOT DO.
 *
 * It does not compute what the learner is charged. `total_minor` and `fee_minor` arrive
 * from the server (R7.1) and are rendered, not derived — the markup formula exists in
 * `previewPathwayTotals` for ONE purpose, an author's live preview of a bundle setting they
 * have not saved yet, and that function's own note says the server's numbers win the moment
 * there are any. A second implementation of `ceil(base × bps / 10000) + fixed` on a learner
 * surface is exactly the second rounding rule rule 2 exists to prevent.
 *
 * It says nothing about promotion codes (R5.9, coupons FR-5.1). There is no code field, no
 * validation call, no after-code price and no code-attributable saving anywhere in this file
 * or on any surface that reads it: the learner types a code into Stripe's own field on
 * Stripe's own page, and thinlms does not know whether they hold one. Every number here is
 * the pre-code price, which is exactly what somebody pays who enters no code.
 *
 * It says nothing about what happens after the Buy button is clicked. That is
 * `docs/specs/thinlms-payments-foundation_spec.md`.
 */

import { openedx, ApiError } from './openedx';
import { getConfig } from './config';
import { courseIdVisibleToTenant } from './catalogue';

const PRICING_API = '/api/pricing/v1';

/**
 * A SECOND BASE PATH, because checkout is served by a DIFFERENT APP.
 *
 * `openedx_thinlms_pricing` owns the price documents; `openedx_thinlms_payments` owns the
 * money. Each mounts its own url_config under its own prefix, so `checkout` is at
 * `/api/payments/v1/checkout` and there is no route at `/api/pricing/v1/checkout` at all
 * (`openedx_thinlms_payments/urls.py`, `checkout_views.CheckoutView`).
 *
 * This constant exists so the two cannot be confused again. `beginCheckout` reached for
 * PRICING_API because both halves of this feature were written on separate branches and the
 * pricing prefix was the only one this file knew — a 404 on the buy click, which
 * `explainPricingFailure` renders as "Pricing is not available on this site yet." That is a
 * refusal rather than a giveaway, so it was safe, but it was also the whole feature dead.
 */
const PAYMENTS_API = '/api/payments/v1';

/**
 * ---------------------------------------------------------------------------------
 * THE ROUTE NAMES, RECONCILED AGAINST THE BACKEND AS BUILT. READ THIS BEFORE EDITING ONE.
 *
 * `openedx_thinlms_pricing/urls.py` registers exactly five routes:
 *
 *     GET  catalogue                     ?course=&pathway=  |  ?page=&page_size=
 *     GET  admin/catalogue               the same, inactive rows included
 *     POST quote
 *     PUT  courses/{course_id}
 *     PUT  pathways/{pathway_key}
 *
 * This file was written against R7.1's names and asked for `prices`, `admin/prices` and
 * `tenant`. NONE OF THE THREE EXISTS, so every read 404'd — and a 404 on an academy with
 * `paid_enrollment` off resolves to an empty-but-valid document in which every course is
 * FREE. The feature was not merely broken, it was broken in the one direction that gives
 * content away, and it would have looked fine on exactly the tenants used to demo it.
 *
 * THE SEMANTICS WERE NEVER IN DISPUTE — only the spelling. `catalogue` takes the same
 * repeatable `?course=` / `?pathway=` parameters this seam already sends, bounded at 100 keys
 * per request against this file's own cap of 50, so the bounded-batch design documented under
 * `MAX_PRICE_BATCH` is exactly what the backend implements. The paths below are therefore
 * corrected to the built names rather than the specified ones.
 *
 * TWO GAPS REMAIN AND NEITHER IS FIXED HERE, because both need an owner's decision:
 *
 *   `tenant`    has no equivalent route. `loadPricingTenant` now asks `catalogue` for the
 *               smallest page there is and reads the tenant block off it — correct, but it
 *               costs one course row to fetch a policy that has nothing to do with courses.
 *               A dedicated route would be one small query instead.
 *   `checkout`  is NOT this backend's. `beginCheckout` belongs to the payments foundation
 *               (sibling F) and 404s until that ships, which surfaces as a stated refusal
 *               before any money moves. `quote` is a different contract and is deliberately
 *               not substituted for it: a quote is not a Checkout Session.
 * ---------------------------------------------------------------------------------
 */

/* ============================================================== the wire ======== */
/*
 * `GET /api/pricing/v1/catalogue` — anonymous, scoped by Host, ONE request for the whole
 * page (R7.1). These interfaces are the contract the pricing backend owes this app, field
 * for field. Every one of them is validated below rather than trusted, because a shape this
 * build does not recognise must degrade to `unknown` and not to a guess (NFR-U4).
 */

/** The tenant's payment posture. Sibling F computes it; this app only renders it. */
export interface WireTenantPayments {
  /**
   * The FIVE-value author-facing projection of sibling F's seven-state lifecycle, computed
   * SERVER-SIDE (spec Assumption 2). Deliberately not re-derived here: a second
   * implementation of a state mapping is the drift this estate keeps paying for.
   */
  payments_state: string;
  /**
   * NULL ON ANY TENANT WITH NO READABLE STRIPE PROFILE, and the same is true of all three fee
   * fields below. Verified against `catalogue.py:_tenant_block`, which emits
   * `profile.currency or None` and `markup.x if markup else None`.
   *
   * The four were originally typed non-nullable here, from R7.1's illustrated block; the
   * implementation is the stricter authority and it is nullable. `readTenant` treats the state
   * as authoritative and these as conditional on it — see its own note.
   */
  currency: string | null;
  /** Basis points. 400 = 4.00%. */
  fee_basis_points: number | null;
  /** The markup's FIXED leg, in minor units. 100 = RM1.00. Not optional — see R5.4. */
  fee_fixed_minor: number | null;
  minimum_charge_minor: number | null;
  paid_enrollment: boolean;
  /**
   * Sibling F's own error sentence, rendered verbatim for the `error` state (R4.4).
   *
   * ADDITIVE to R7.1's illustrated block and emitted by the implementation. Never invented and
   * never paraphrased here.
   */
  last_error_message?: string | null;
}

export interface WireCoursePrice {
  course_id: string;
  mode: string;
  base_minor: number | null;
  fee_minor: number | null;
  total_minor: number | null;
  currency: string | null;
  active: boolean;
  /**
   * True only when the price exists in Stripe at this exact amount. False means thinlms
   * holds a price nothing can charge, so the surface shows the price and refuses the sale
   * BEFORE the click instead of after it (R6.10, AC-003b).
   */
  price_synced: boolean;
  /**
   * WHY THIS COURSE CANNOT BE PRICED, as a reason code — and it is emitted on COURSE rows, not
   * only on pathways. Verified against `catalogue.py:_course_payload`.
   *
   * `errors.py` defines six codes: `membership_unreadable`, `currency_mismatch`, `not_on_sale`,
   * `gate_closed`, `amount_invalid`, `payment_profile_unreadable`. `reasonSentence` maps the
   * ones that have their own learner-facing sentence and falls back to the generic one for the
   * rest, which is a less specific message but never a wrong one.
   */
  unavailable_reason?: string | null;
  /**
   * The BACKEND'S OWN three-way resolution: `free`, `paid` or `unavailable`.
   *
   * Read as the authority where present, because it is the server's answer to the same question
   * `readCourseRow` would otherwise re-derive from `mode` + `active` + the amounts — and a
   * second implementation of one state mapping is the drift this file exists to avoid. Optional
   * because a backend predating it simply omits it, in which case the local derivation stands.
   */
  effective_mode?: string;
}

export interface WirePathwayPrice {
  pathway_key: string;
  mode: string;
  discount_basis_points: number | null;
  /** Σ member BASES. The input to the bundle modes, and what a percentage applies to. */
  separate_base_minor: number | null;
  /** Σ member bases EACH MARKED UP — what buying the parts one at a time really costs. */
  separate_minor: number | null;
  bundle_minor: number | null;
  base_minor: number | null;
  fee_minor: number | null;
  total_minor: number | null;
  saving_minor: number | null;
  currency: string;
  active: boolean;
  member_count: number | null;
  paid_member_count: number | null;
  price_synced: boolean;
  /** `membership_unreadable`, `currency_mismatch`, … or null. Rendered, never invented. */
  unavailable_reason: string | null;
}

export interface WirePricingCatalogue {
  tenant: WireTenantPayments;
  courses: WireCoursePrice[];
  pathways: WirePathwayPrice[];
}

/* ============================================================== view types ====== */

export type PriceMode = 'free' | 'paid' | 'unknown';

/**
 * What any surface needs to render a price. Nothing here is optional-by-accident.
 *
 * TWO FIELDS BEYOND THE SPEC'S SKETCH, both because copy the spec itself mandates has to
 * print an amount: `feeMinor` for R5.4's *"Includes RM 6.00 payment processing"*, and
 * `baseMinor` for R6.7's "Bundle price (your net)" row. Deriving either from `totalMinor`
 * inside a component would be arithmetic on money outside this file.
 */
export interface PriceView {
  mode: PriceMode;
  /** What the learner pays, markup included. Null iff mode !== 'paid'. */
  totalMinor: number | null;
  /** The academy's own net, before the processing markup. Null iff mode !== 'paid'. */
  baseMinor: number | null;
  /** The processing markup inside `totalMinor`. Null iff mode !== 'paid'. */
  feeMinor: number | null;
  /** Undiscounted comparison price, or null when there is nothing to compare to. */
  wasMinor: number | null;
  savingMinor: number | null;
  currency: string | null;
  /**
   * False when this price cannot be bought. Always true for `free`, always false for
   * `unknown`.
   *
   * IT IS NO LONGER FALSE FOR AN UNSYNCED PRICE, AND THAT IS A DELIBERATE DEPARTURE FROM
   * AC-003b / R6.10. The spec's rule was `price_synced: false` -> unavailable, and it created
   * a deadlock: the payments app self-heals a missing or wrong Stripe object at checkout
   * (`catalog.resolve_for_checkout`, `OrderEvent.CATALOG_REPAIRED`), but the repair only runs
   * if a learner reaches checkout, and this rule was what stopped them. The mechanism that
   * would have fixed the problem sat behind a door only it could open, and on dev that meant
   * every priced course was permanently unbuyable.
   *
   * WHAT MAKES IT SAFE IS THAT THE SERVER HAS ALREADY REFUSED EVERYTHING IT CAN. By the time
   * `readCourseRow` reads `price_synced` it has passed the server's own `unavailable_reason` —
   * `gate_closed`, `payment_profile_unreadable`, `currency_mismatch`, `not_on_sale`,
   * `amount_invalid`, `below_minimum_charge` — and `mode: 'paid'` with valid amounts. Those
   * are exactly the states checkout refuses; what is left is exactly the states checkout
   * REPAIRS. So an unsynced price here means "the Stripe object is missing or stale", which is
   * a repair, and every genuine refusal is still a refusal before the click.
   *
   * And the failure direction is unchanged if that reasoning is ever wrong: checkout refuses
   * with the server's own sentence and NOTHING IS CHARGED. It never resolves in the learner's
   * favour and it never gives paid content away.
   */
  buyable: boolean;
  /**
   * Why this cannot be bought, in words a learner can read, or null when it can.
   *
   * Populated for `unknown` AND for a known-but-unbuyable price, because those are two
   * different sentences and both have to reach the screen: AC-003b renders the price WITH
   * the reason, AC-016 renders the reason INSTEAD of a price.
   */
  reason: string | null;
  /**
   * The price is buyable and its Stripe object is not up to date — so checkout will repair it
   * on the way through.
   *
   * FOR THE AUTHOR, NOT THE LEARNER. A learner can do nothing with this and does not need to:
   * they see a normal price and the purchase works. An author looking at their own course
   * settings is the person who benefits from knowing the item is not on Stripe yet, because a
   * row that STAYS in this state is a sync that keeps failing and there is a `state=error`
   * catalogue link and a log line waiting for them.
   *
   * Always false for `free` and for `unknown` — there is nothing to sync and nothing to buy.
   */
  syncPending: boolean;
}

/** Extra facts a pathway price carries that a course price cannot. Display only. */
export interface PathwayPriceView extends PriceView {
  /** `separate_base_minor` — what a percentage discount applies to (R2.2). */
  separateBaseMinor: number | null;
  /** `separate_minor` — the post-markup buy-separately total (R2.2). */
  separateMinor: number | null;
  discountBasisPoints: number | null;
  memberCount: number | null;
  paidMemberCount: number | null;
  /** The wire's own mode, so the authoring panel can seed its form from the saved row. */
  wireMode: PathwayMode | null;
}

export type CourseMode = 'free' | 'paid';
export type PathwayMode = 'free' | 'sum' | 'fixed' | 'percent_discount';

const PATHWAY_MODES: readonly string[] = ['free', 'sum', 'fixed', 'percent_discount'];

/** The five author-facing states (spec Assumption 2). Anything else is treated as `error`. */
export type PaymentsState =
  | 'not_configured'
  | 'pending_verification'
  | 'verified'
  | 'error'
  | 'disabled';

const PAYMENTS_STATES: readonly string[] = [
  'not_configured',
  'pending_verification',
  'verified',
  'error',
  'disabled',
];

export interface TenantPayments {
  state: PaymentsState;
  /**
   * The academy's charging currency, or `''` when it has no payment account yet.
   *
   * Empty rather than null because it is only ever read to format a number, and there are no
   * numbers to format without a profile. Every fee field below is 0 in the same case — see
   * `readTenant` for why the state is authoritative and these are conditional on it.
   */
  currency: string;
  feeBasisPoints: number;
  feeFixedMinor: number;
  minimumChargeMinor: number;
  paidEnrollment: boolean;
  /** The tenant record's own error sentence, verbatim, for the `error` state (R4.4). */
  lastErrorMessage: string | null;
}

/**
 * The whole pricing document, validated once.
 *
 * `ok: false` is the single fact every surface branches on for the failure case, and it is
 * why the maps are still present and empty rather than absent: a component that forgot to
 * check `ok` gets `unknown` from the lookups anyway, which is the safe answer rather than a
 * crash (NFR-U4).
 */
export interface PricingSeam {
  ok: boolean;
  tenant: TenantPayments | null;
  courses: Map<string, PriceView>;
  pathways: Map<string, PathwayPriceView>;
  /** Why the document is unusable. Null when `ok`. */
  reason: string | null;
}

/* ================================================================== copy ======== */
/*
 * EVERY LEARNER-FACING SENTENCE ABOUT MONEY, IN ONE PLACE.
 *
 * Not for reuse's sake — for AC-003c. The processing markup covers the ACADEMY's Stripe
 * charge and settles to the academy; Mereka takes no commission and receives none of it
 * (D5). So "platform fee", "service fee", "booking fee", "commission", "Mereka charge" and
 * "tax" are all factually wrong here, and the permitted words are "payment processing".
 * Collecting the strings in one object is what makes that greppable rather than hopeful.
 *
 * Nothing here mentions a promotion code, a period, a subscription or a trial: prices are
 * one-time (D6, R5.8) and codes are Stripe's (D4, R5.9).
 */
export const PRICE_COPY = {
  free: 'Free',
  unavailable: 'Price unavailable',
  /** Always paired with a reason — "Price unavailable" is never alone (NFR-A2). */
  nothingCharged: 'Nothing has been charged.',
  processingShort: 'Includes payment processing.',
  boughtSeparately: 'Bought separately',
  save: 'Save',
  notOnSale: 'This is not on sale at the moment. Nothing has been charged.',
  /**
   * THE AUTHOR'S sentence, and no longer the learner's.
   *
   * It used to be the learner's `reason` whenever `price_synced` was false, paired with a
   * disabled Buy button — which is the deadlock this branch removed: checkout is what repairs
   * an unsynced Stripe Price, so a learner who cannot reach checkout can never get the course
   * out of that state. The learner now sees a normal price and a working button. This sentence
   * moved to the author's own course settings, where "not on Stripe yet" is something the
   * reader can actually act on.
   */
  notSynced:
    'This price is not on Stripe yet. Learners can still buy it — the payment step puts it there — but if this does not clear, the sync is failing and a platform administrator should look.',
  /**
   * `below_minimum_charge` — the charged total is under the smallest amount this academy's
   * Stripe account will process.
   *
   * Its own sentence rather than the generic one because there IS something to say: the amount
   * is the problem, and it is a fact about the academy's payment account rather than about the
   * learner. It says nothing has been charged, because nothing has.
   */
  belowMinimum:
    'This is priced below the smallest amount this academy’s payment provider will process, so it cannot be bought. Nothing has been charged — contact the academy.',
  /**
   * `gate_closed` and `payment_profile_unreadable` — the academy cannot take money yet.
   *
   * Deliberately says nothing about Stripe, verification or configuration: that is the
   * AUTHOR's gate copy (`gateNotice`), and a learner can act on none of it. What a learner
   * needs is that this is not their fault, nothing has been charged, and who to ask.
   */
  notSetUp:
    'This academy is not taking payments yet, so this cannot be bought. Nothing has been charged — contact the academy if you were expecting to buy it.',
  payloadFailed:
    'We could not read prices just now, so nothing here can be bought. Nothing has been charged — reload the page to try again.',
  membershipUnreadable:
    'We could not read the courses in this path, so we cannot show its price. Nothing has been charged.',
  currencyMismatch:
    'The courses in this path are priced in different currencies, so no total can be shown. Nothing has been charged.',
  unknownMode:
    'This price is in a form this version of the site does not understand. Nothing has been charged.',
  cannotBuyCourse:
    'This course cannot be bought right now. Nothing has been charged. Try again shortly, or contact the academy.',
  cannotBuyPathway:
    'This learning path cannot be bought right now. Nothing has been charged. Try again shortly, or contact the academy.',
  pending:
    'We are waiting for your payment to confirm. This usually takes a few seconds — nothing more is needed from you. Reload the page.',
} as const;

/**
 * The gate, per state (R4.4): a pill and a sentence that names the reason AND who to ask.
 *
 * The shape `CourseSettings.tsx:304-328` and `DomainsCard.tsx:188-237` already use — a
 * state pill, the reason in words, and no invented verdict — rather than a hidden section.
 * `null` means the gate holds and pricing is editable.
 */
export interface GateNotice {
  pill: string;
  note: string;
}

export function gateNotice(tenant: TenantPayments | null): GateNotice | null {
  if (!tenant) {
    return {
      pill: 'Payments unavailable',
      note: 'We could not read this academy’s payment settings, so pricing cannot be changed right now. Nothing has been changed. Reload the page, and ask a platform administrator if it keeps happening.',
    };
  }

  // The flag half of R4.1, checked first when the state itself is fine: an academy whose
  // Stripe is verified and whose paid enrolment is off needs the sentence about the
  // setting, not the one about Stripe.
  if (tenant.state === 'verified' && !tenant.paidEnrollment) {
    return {
      pill: 'Paid enrolment off',
      note: 'Paid enrolment is switched off in this academy’s settings. Turn on Paid enrolment to price courses.',
    };
  }

  switch (tenant.state) {
    case 'verified':
      return null;
    case 'not_configured':
      return {
        pill: 'Payments not set up',
        note: 'This academy has not connected a payment account, so courses cannot be sold yet. Ask a platform administrator to connect Stripe for this academy.',
      };
    case 'pending_verification':
      return {
        pill: 'Verifying payment account',
        note: 'Stripe is still verifying this academy’s payment account. Pricing unlocks as soon as it is verified — nothing you need to do.',
      };
    case 'disabled':
      return {
        pill: 'Payments turned off',
        note: 'Paid enrolment is switched off for this academy. A platform administrator can turn it back on.',
      };
    case 'error':
      return {
        pill: 'Payment account needs attention',
        note: 'Ask a platform administrator to review the payment account.',
      };
  }
}

/**
 * Both halves of R4.1 — the flag AND the state — in one predicate, so no screen implements
 * half of it. The server refuses the write either way (R4.5, 409); this is the courtesy.
 */
export function pricingEditable(tenant: TenantPayments | null): boolean {
  return tenant !== null && tenant.state === 'verified' && tenant.paidEnrollment;
}

/* ============================================================= formatting ======= */

/**
 * Minor-unit digits per currency — a LOOKUP, never a constant 2 (R5.3).
 *
 * Hard-coding 2 renders ¥1,290 as ¥12.90, which is the same class of bug as rendering a
 * missing price as free: a number that looks perfectly plausible and is wrong by a factor of
 * a hundred. The deployment is MYR-first, so this table has to be CORRECT, not complete; an
 * unknown currency falls back to 2 and says so, because a silent fallback is how the JPY
 * case ships.
 */
const MINOR_UNIT_DIGITS: Record<string, number> = {
  BHD: 3,
  BIF: 0,
  CLP: 0,
  IDR: 0,
  JOD: 3,
  JPY: 0,
  KRW: 0,
  KWD: 3,
  MYR: 2,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  SGD: 2,
  THB: 2,
  TND: 3,
  UGX: 0,
  USD: 2,
  VND: 0,
  XAF: 0,
  XOF: 0,
  XPF: 0,
};

const warnedCurrencies = new Set<string>();

export function minorUnitDigits(currency: string): number {
  const code = currency.toUpperCase();
  const digits = MINOR_UNIT_DIGITS[code];
  if (digits !== undefined) return digits;

  // Once per currency per session. A per-render log over a catalogue of twelve cards is
  // noise nobody reads, and noise nobody reads is the same as no log at all.
  if (!warnedCurrencies.has(code)) {
    warnedCurrencies.add(code);
    console.warn(
      `[pricing] No minor-unit digit count for ${code}; assuming 2. Add it to MINOR_UNIT_DIGITS in shared/lib/pricing.ts.`,
    );
  }
  return 2;
}

/**
 * The tenant's locale, NOT the browser's (R5.2).
 *
 * `formatDate` at `catalogue.ts:717` passes `undefined` and therefore renders in whatever
 * locale the reader's machine happens to be set to — cosmetic on a date, and a
 * symbol-placement difference on money. `config.locale.default` is the academy's own choice
 * and is what the rest of the page is written in.
 *
 * Wrapped, because `getConfig()` throws before `loadConfig()` resolves. A price cannot
 * render before the app has booted in practice, but a throw from a formatter would take a
 * whole page down over a missing locale — so the fallback is 'en' and the price still shows.
 */
function localeForMoney(): string {
  try {
    return getConfig().locale?.default || 'en';
  } catch {
    return 'en';
  }
}

/**
 * Minor units to characters. THE ONLY `Intl.NumberFormat` IN THE APP (AC-029).
 *
 * `narrowSymbol` so MYR reads "RM 131.00" rather than "MYR 131.00". Grouping and symbol
 * placement are `Intl`'s, per locale, never hand-assembled — a hand-assembled `RM ${x}` is
 * correct until the first tenant on a locale that puts the symbol after the number.
 *
 * TRAILING `.00` IS KEPT ON PURPOSE. A price that shows cents on one card and not on the
 * next reads as two different prices.
 */
export function formatMoney(minor: number, currency: string): string {
  const digits = minorUnitDigits(currency);
  const major = minor / 10 ** digits;

  try {
    return new Intl.NumberFormat(localeForMoney(), {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(major);
  } catch {
    // An invalid currency code from a backend we do not control must not throw inside a
    // render. `Intl` rejects anything that is not three letters, so this is the shape a
    // malformed payload takes if it ever gets past validation.
    return `${currency} ${major.toFixed(digits)}`;
  }
}

/**
 * "Free" | "RM 131.00" | "Price unavailable" — the one string a card shows.
 *
 * `"Free"` IS A WORD, NEVER A ZERO (R5.7). `formatMoney(0)` renders "RM 0.00", which on a
 * card reads as a broken price rather than as an invitation.
 */
export function priceLabel(view: PriceView): string {
  if (view.mode === 'free') return PRICE_COPY.free;
  if (view.mode === 'paid' && view.totalMinor !== null && view.currency) {
    return formatMoney(view.totalMinor, view.currency);
  }
  return PRICE_COPY.unavailable;
}

/**
 * The disclosure that sits next to every paid price (R5.4), naming the amount.
 *
 * The words are fixed by D5: the markup covers the ACADEMY's payment-processing charge and
 * settles to the academy. It is not a platform fee, not a commission, not a Mereka charge
 * and not tax. Returns null for anything that is not a paid price with a known fee, so a
 * caller can render it unconditionally.
 */
export function processingNote(view: PriceView): string | null {
  if (view.mode !== 'paid' || view.feeMinor === null || !view.currency) return null;
  return `Includes ${formatMoney(view.feeMinor, view.currency)} payment processing. This is the full amount you will be charged.`;
}

/**
 * "was RM 539.64, now RM 457.15" — the label a screen reader needs on a struck price.
 *
 * NFR-A1: two bare numbers with no stated relation is worse than one number, because a
 * reader hears the higher one and cannot tell which of the two applies to them.
 */
export function comparisonLabel(view: PriceView): string | null {
  if (view.wasMinor === null || view.totalMinor === null || !view.currency) return null;
  return `was ${formatMoney(view.wasMinor, view.currency)}, now ${formatMoney(view.totalMinor, view.currency)}`;
}

/* ============================================================ buy states ======== */

/**
 * The Enrol-vs-Buy state machine (R6.10). SIX states, resolved HERE and rendered by the
 * call sites, for the reason `paths.ts:217-223` gives about deriving a state once:
 * "Deriving it once here is what stops the list page and the detail page disagreeing."
 */
export type BuyState = 'enrol' | 'buy' | 'sign_in_to_buy' | 'enter' | 'pending' | 'unavailable';

export interface BuyContext {
  view: PriceView;
  signedIn: boolean;
  /** Enrolled, purchased, or given access by an admin — all the same fact to this page. */
  owned: boolean;
  /**
   * Sibling F's fulfilment status says a payment is in flight. NEVER inferred from the
   * absence of an enrolment (R6.10): absence is also what a FAILED payment looks like, and
   * telling somebody their payment is processing when it failed is worse than saying
   * nothing. Until sibling F exists this is always false, which is the honest default.
   */
  paymentPending?: boolean;
}

export function buyState({ view, signedIn, owned, paymentPending = false }: BuyContext): BuyState {
  // OWNERSHIP OUTRANKS EVERYTHING, including a tenant whose Stripe has broken. A learner
  // who already holds the thing keeps seeing "Go to course" (R6.6, AC-008, AC-022);
  // recomputing their access from a price is how a payment outage locks paying customers out
  // of content they own.
  if (owned) return 'enter';

  // `unknown` and "known but not purchasable" both land here, and they carry different
  // sentences in `view.reason` — see `PriceView.reason`.
  if (view.mode === 'unknown' || !view.buyable) return 'unavailable';

  if (view.mode === 'free') return 'enrol';

  if (paymentPending) return 'pending';
  if (!signedIn) return 'sign_in_to_buy';
  return 'buy';
}

/* =========================================================== the seam ========== */

/**
 * ---------------------------------------------------------------------------------
 * THE SEAM IS A BOUNDED BATCH READ, AND THAT IS A DELIBERATE DEPARTURE FROM THE SPEC.
 *
 * R7.1 specifies ONE request returning the tenant's WHOLE price catalogue with no
 * pagination, arguing from `catalogue.ts:200-221` and `paths.ts:541-561`. That argument is
 * correct about the failure it names — a per-card request — and it was measured on an
 * academy with seventeen pathways. The owner's target is **1000 courses and 1000 pathways
 * per tenant**, and at that size the whole-collection payload is the other failure mode,
 * already present in this codebase: `/api/pathways/v1/learner/paths` returns every pathway
 * with every step, and `PATH_SEAM_KEY`'s "free detail page" means rendering ONE pathway
 * requires having downloaded ALL of them.
 *
 * So this seam takes the middle road, which is neither of the two failures:
 *
 *   ONE request per SCREENFUL, keyed by the items on it.
 *
 *     GET /api/pricing/v1/prices?course=<id>&course=<id>&pathway=<key>…   ≤ MAX_PRICE_BATCH
 *     GET /api/pricing/v1/tenant                                          the gate alone
 *
 * Consequences, each of which is a rule the surfaces keep:
 *
 *   - A grid of 24 admin rows asks for 24 prices, not 1000. A catalogue page of 12 asks for
 *     12. The request grows with the SCREEN, not with the academy.
 *   - A DETAIL PAGE ASKS FOR ITS OWN ITEM — a batch of one — and therefore never depends on
 *     a list payload being in cache. A cold landing on a course or pathway URL costs one
 *     small request, not the collection. That is the property `PATH_SEAM_KEY` gave up.
 *   - There is still no per-card request. One batch per screen, never one per row.
 *
 * `MAX_PRICE_BATCH` is 50 because these are GET keys in a query string: a course key is
 * ~40 characters percent-encoded, so 50 of them is a ~2 KB URL, comfortably inside every
 * proxy limit in the path (Caddy, nginx-ingress and Django all default to 8 KB). A caller
 * with more keys than that is chunked below rather than truncated — silently dropping keys
 * would render a price as "unavailable" for reasons no log would explain.
 * ---------------------------------------------------------------------------------
 */

/** Keys per request. See the note above for why it is this number and not a rounder one. */
export const MAX_PRICE_BATCH = 50;

/**
 * The ROOT React Query key for everything priced, and the only thing a write has to
 * invalidate to make every surface agree again.
 *
 * `PRICING_SEAM_KEY` in the spec is `(signedIn: boolean) => [...]`, by analogy with
 * `PATH_SEAM_KEY` at `paths.ts:353-366`. THE ANALOGY DOES NOT HOLD AND THE PARAMETER IS
 * DROPPED, for two reasons that both matter at scale:
 *
 *   1. The answer does not differ. A pathway list is per-learner — it carries
 *      `enrollment_date` — so caching a visitor's `[]` under a learner's key would show an
 *      empty catalogue after signing in. A PRICE is the same integer for everybody; R7.1's
 *      payload has no per-learner field at all. Ownership is a different query on every
 *      surface that needs it.
 *   2. `signedIn` is derived from a session query and is `false` before it resolves, so a
 *      key containing it CHANGES IDENTITY mid-load and fetches the same prices twice on
 *      every cold page. That is a live suspect at `CourseHome.tsx:122-123` and
 *      `PathDetail.tsx:89-90`, and it is not worth reproducing for a distinction that is
 *      not real.
 */
export const PRICING_SEAM_KEY = ['pricing'] as const;

/** The tenant gate and fee policy — one small row, shared by every surface that asks. */
export const PRICING_TENANT_KEY = ['pricing', 'tenant'] as const;

/**
 * The key for one batch. A STRING, not an array of keys.
 *
 * React Query hashes the key structurally, so an inline array would be fine for equality —
 * but every caller builds its list with `.map()` inside a render, and passing that array
 * straight through means the key's contents are recomputed (and re-hashed) on every render
 * of a 1000-row table. One sorted, joined string is computed once by the caller's `useMemo`
 * and compared as a scalar.
 *
 * SORTED, so two screens showing the same rows in a different order share one cache entry
 * rather than fetching the same prices twice.
 *
 * JOINED ON A DELIMITER THAT CANNOT APPEAR IN A KEY, and that is not a detail. With an EMPTY
 * separator the string is ambiguous: `['ab', 'c']` and `['a', 'bc']` produce the same key, so
 * two screens asking about different courses would share one cache entry and one of them
 * would render the other's prices. A wrong price on a page is the single failure this module
 * exists to prevent. The delimiter is written as an ESCAPE rather than as a literal control
 * character, because a literal one is invisible in review and makes git treat the file as
 * binary — which is how this line was wrong in two different ways at once.
 *
 * `course-v1:ORG+NUM+RUN` and `path-v1:org+number+run+group` are opaque platform identifiers
 * with no control characters in them, and `dedupe` has already dropped the empties.
 */
const KEY_DELIMITER = '\u0000';

export const pricesQueryKey = (kind: 'courses' | 'pathways', keys: readonly string[]) =>
  ['pricing', kind, [...keys].sort().join(KEY_DELIMITER)] as const;

/** The seam a page gets when there is no usable document. Every item on it is `unknown`. */
function failedSeam(reason: string): PricingSeam {
  return { ok: false, tenant: null, courses: new Map(), pathways: new Map(), reason };
}

/** The seam for an install with no pricing app at all. See `loadPrices`. */
function unpricedSeam(tenant: TenantPayments | null): PricingSeam {
  return { ok: true, tenant, courses: new Map(), pathways: new Map(), reason: null };
}

export interface PriceRequest {
  courseIds?: readonly string[];
  pathwayKeys?: readonly string[];
}

/**
 * Prices for the items on ONE screen. NEVER REJECTS.
 *
 * NEVER REJECTS is a contract, not a convenience. Two reasons:
 *
 *   1. A 401 anywhere in this app triggers a WHOLE-APP REDIRECT to /login (see
 *      `setUnauthenticatedHandler` in main.tsx), so a pricing read that threw would throw a
 *      signed-out visitor off the public catalogue — the trap `paths.ts:294-309` documents.
 *   2. Every failure has to arrive at the components as `mode: 'unknown'`, not as a query
 *      error that nine surfaces each have to remember to branch on (NFR-U4, AC-016).
 *
 * CHUNKED, NOT TRUNCATED. More keys than `MAX_PRICE_BATCH` become several requests whose
 * results are merged. In practice no surface asks for more than a page: the chunking is
 * there so that a caller who does gets correct prices rather than silently missing ones.
 * The chunks are issued together — they are small, cacheable GETs, and a screenful is at
 * most a handful of them.
 *
 * ---------------------------------------------------------------------------------
 * THE 404 RULE, WHICH IS THE ONE JUDGEMENT CALL IN THIS FILE AND IS DELIBERATELY NARROW.
 *
 * `openedx_thinlms_pricing` is not on every install (rollout Phase 0/1), and without it this
 * endpoint 404s. Treating that as a failure would make every course on every existing tenant
 * read "Price unavailable" instead of "Free" — a visible regression on a product where
 * nothing is priced yet, and the opposite of the rollout's requirement that in Phase 1 "the
 * diff should be invisible in the browser".
 *
 * So a 404 — and ONLY a 404 — resolves to an EMPTY-BUT-VALID document, in which every item
 * asked about is absent from a payload that loaded and is therefore free (R6.0, row 1).
 *
 * AND IT IS GATED ON THE TENANT SELLING NOTHING. `paid_enrollment` comes from the tenant
 * config the app has already loaded, not from this endpoint, so it is readable even when this
 * endpoint is not. If a tenant HAS paid enrolment on, a 404 means a pricing service that
 * should be answering is not, and every item on the page becomes `unknown`. That is the
 * narrowest rule that keeps an unpriced install unchanged without ever giving away a course
 * on a tenant that sells them.
 *
 * A 500, a timeout, a refusal, or a body that fails validation is a failure and is treated as
 * one, whatever the tenant's flag says.
 */
export async function loadPrices(request: PriceRequest): Promise<PricingSeam> {
  const courses = dedupe(request.courseIds);
  const pathways = dedupe(request.pathwayKeys);

  // Nothing to ask about. A valid, empty document — not a request, and not a failure.
  if (courses.length === 0 && pathways.length === 0) return unpricedSeam(null);

  const batches = planBatches(courses, pathways);
  const seams = await Promise.all(batches.map(readBatch));

  return mergeSeams(seams);
}

/** One item's price, for a detail page landed on cold. A batch of one, by construction. */
export const loadCoursePrice = (courseId: string) => loadPrices({ courseIds: [courseId] });
export const loadPathwayPrice = (pathwayKey: string) => loadPrices({ pathwayKeys: [pathwayKey] });

/**
 * The tenant gate and fee policy on its own.
 *
 * Needed by the surfaces that must render the gate before any item exists — the pathway
 * authoring panel on a path being created, and the course Pricing card on a course with no
 * price row yet. One tiny row, one query key, so every one of them shares it.
 */
export async function loadPricingTenant(): Promise<PricingSeam> {
  let body: unknown;
  try {
    /**
     * THERE IS NO `tenant` ROUTE, so this asks `catalogue` for the smallest page it will
     * serve and keeps only the tenant block off it.
     *
     * `page_size=1` rather than no parameters at all: `parse_selection` defaults to a page of
     * 50 when nothing names a key, so the bare URL would fetch fifty course rows to read six
     * numbers about the academy. One row is the floor the backend allows — `_positive_int`
     * refuses 0 — so this is one row of waste, not fifty.
     *
     * The row itself is DISCARDED rather than merged. It is whichever course sorts first by
     * key, which has nothing to do with any screen asking this question, and seeding the
     * course map from it would put one arbitrary price into a cache entry that every surface
     * shares under a key that does not mention it.
     */
    body = await openedx.get<unknown>(`${PRICING_API}/catalogue?page_size=1`, true);
  } catch (error) {
    if (error instanceof ApiError && error.status === 404 && !tenantSellsAnything()) {
      return unpricedSeam(null);
    }
    reportPayloadRejected(error instanceof ApiError ? `tenant: HTTP ${error.status}` : 'tenant read failed');
    return failedSeam(PRICE_COPY.payloadFailed);
  }
  if (body === null || body === undefined) {
    if (!tenantSellsAnything()) return unpricedSeam(null);
    return failedSeam(PRICE_COPY.payloadFailed);
  }
  // The envelope's own tenant block, with the sampled row dropped on the floor.
  const envelope = isRecord(body) ? body : {};
  return readPricingSeam({ tenant: envelope['tenant'], courses: [], pathways: [] });
}

/** Sorted, de-duplicated, empties dropped — so a key is stable whatever order it arrived in. */
function dedupe(keys: readonly string[] | undefined): string[] {
  if (!keys || keys.length === 0) return [];
  return [...new Set(keys.filter((key) => typeof key === 'string' && key !== ''))].sort();
}

/**
 * Split a screenful into requests of at most `MAX_PRICE_BATCH` keys.
 *
 * Courses and pathways travel together where they fit, because a course page asks about one
 * course AND the pathways it belongs to and that should be one request, not two.
 */
function planBatches(courses: string[], pathways: string[]): PriceRequest[] {
  const batches: PriceRequest[] = [];
  let courseCursor = 0;
  let pathwayCursor = 0;

  while (courseCursor < courses.length || pathwayCursor < pathways.length) {
    const courseSlice = courses.slice(courseCursor, courseCursor + MAX_PRICE_BATCH);
    courseCursor += courseSlice.length;
    const room = MAX_PRICE_BATCH - courseSlice.length;
    const pathwaySlice = pathways.slice(pathwayCursor, pathwayCursor + room);
    pathwayCursor += pathwaySlice.length;

    batches.push({ courseIds: courseSlice, pathwayKeys: pathwaySlice });
    // A batch that could take nothing would loop for ever. `room` is only 0 when the course
    // slice filled the batch, in which case the cursor moved, so this cannot happen — but
    // the guard is cheaper than the hang it prevents.
    if (courseSlice.length === 0 && pathwaySlice.length === 0) break;
  }

  return batches;
}

async function readBatch(request: PriceRequest): Promise<PricingSeam> {
  const params = new URLSearchParams();
  for (const id of request.courseIds ?? []) params.append('course', id);
  for (const key of request.pathwayKeys ?? []) params.append('pathway', key);

  let body: unknown;
  try {
    // `true` = treat 401/403 as a value rather than an error. This endpoint must answer a
    // signed-out visitor: Discover, Landing and every course page render for one.
    // `catalogue`, not `prices`: see the route note at the top of this file.
    body = await openedx.get<unknown>(`${PRICING_API}/catalogue?${params}`, true);
  } catch (error) {
    if (error instanceof ApiError && error.status === 404 && !tenantSellsAnything()) {
      return unpricedSeam(null);
    }
    reportPayloadRejected(error instanceof ApiError ? `HTTP ${error.status}` : 'the request failed');
    return failedSeam(PRICE_COPY.payloadFailed);
  }

  // `allowUnauthenticated` turns 401/403 into null. A price document nobody may read is not
  // a free catalogue.
  if (body === null || body === undefined) {
    if (!tenantSellsAnything()) return unpricedSeam(null);
    reportPayloadRejected('the pricing service refused the request');
    return failedSeam(PRICE_COPY.payloadFailed);
  }

  return readPricingSeam(body);
}

/**
 * Several batches into one seam.
 *
 * ONE FAILED BATCH FAILS THE WHOLE SEAM, and that is the safe direction: a page holding
 * half its prices would render the other half as free, which is R6.0's revenue bug arriving
 * by a different route. A page that could not read all of its prices reads none of them.
 */
function mergeSeams(seams: PricingSeam[]): PricingSeam {
  const failed = seams.find((seam) => !seam.ok);
  if (failed) return failedSeam(failed.reason ?? PRICE_COPY.payloadFailed);

  const merged: PricingSeam = {
    ok: true,
    tenant: seams.find((seam) => seam.tenant !== null)?.tenant ?? null,
    courses: new Map(),
    pathways: new Map(),
    reason: null,
  };
  for (const seam of seams) {
    for (const [id, view] of seam.courses) merged.courses.set(id, view);
    for (const [key, view] of seam.pathways) merged.pathways.set(key, view);
  }
  return merged;
}

/**
 * Does this tenant sell anything at all?
 *
 * Read from the tenant config (`config/features.yaml:23`, `paid_enrollment`), which arrives
 * in a DIFFERENT request from the pricing document — which is the whole point: it is still
 * answerable when the pricing service is not. `false` on any error, because "we could not
 * tell" and "this academy does not sell" are the same posture for the 404 rule above, and it
 * is the safe one.
 */
function tenantSellsAnything(): boolean {
  try {
    return getConfig().features?.['paid_enrollment'] === true;
  } catch {
    return false;
  }
}

/**
 * VALIDATE THE DOCUMENT ONCE, HERE (NFR-U4). Exported because the component tests seed this
 * seam directly rather than mocking a fetch, and because it is the unit worth testing.
 *
 * Anything not fully understood becomes `unknown` for that row — or, when it is the tenant
 * block that is wrong, for the whole document. No component does its own narrowing, so there
 * is exactly one place where "a newer backend sent something else" is decided.
 */
export function readPricingSeam(body: unknown): PricingSeam {
  if (!isRecord(body)) {
    reportPayloadRejected('the body is not an object');
    return failedSeam(PRICE_COPY.payloadFailed);
  }

  const tenant = readTenant(body['tenant']);
  if (tenant === null) {
    // The tenant block carries the currency and both markup legs. Without it a price is an
    // integer with no unit, and an integer with no unit must never be rendered as money.
    reportPayloadRejected('the tenant block is missing or malformed');
    return failedSeam(PRICE_COPY.payloadFailed);
  }

  const courses = new Map<string, PriceView>();
  for (const row of asArray(body['courses'])) {
    const entry = readCourseRow(row, tenant);
    if (entry) courses.set(entry[0], entry[1]);
  }

  const pathways = new Map<string, PathwayPriceView>();
  for (const row of asArray(body['pathways'])) {
    const entry = readPathwayRow(row, tenant);
    if (entry) pathways.set(entry[0], entry[1]);
  }

  return { ok: true, tenant, courses, pathways, reason: null };
}

function readTenant(raw: unknown): TenantPayments | null {
  if (!isRecord(raw)) return null;

  const currency = asCurrency(raw['currency']);
  const bps = asWholeNumber(raw['fee_basis_points']);
  const fixed = asWholeNumber(raw['fee_fixed_minor']);
  const floor = asWholeNumber(raw['minimum_charge_minor']);

  /**
   * `payments_state` MUST BE PRESENT. Its absence is a malformed payload, not a state.
   *
   * The distinction matters because everything below tolerates missing fee fields, and without
   * this line a tenant block of `{}` would read as a usable document — which would make every
   * course ABSENT from that document free (R6.0, row 1), turning a malformed payload into a
   * giveaway. The backend emits this key on every response, so requiring it costs nothing and
   * keeps "we could not read this" separate from "this academy has no payment account".
   */
  const raw_state = raw['payments_state'];
  if (typeof raw_state !== 'string' || raw_state === '') return null;

  // A state string this build does not recognise is `error`, never `verified`. A newer backend
  // inventing a state must not unlock pricing, and the author's action for "we do not know" is
  // the same as for "something is wrong": ask an administrator.
  const state: PaymentsState = PAYMENTS_STATES.includes(raw_state)
    ? (raw_state as PaymentsState)
    : 'error';

  /**
   * THE FEE POLICY IS ABSENT ON ANY TENANT WITH NO PAYMENT ACCOUNT, AND THAT IS NOT A FAILURE.
   *
   * The backend emits `currency`, `fee_basis_points`, `fee_fixed_minor` and
   * `minimum_charge_minor` as NULL whenever there is no readable Stripe profile — which is
   * every tenant in `not_configured`, i.e. every academy before anyone connects Stripe. This
   * function used to refuse such a payload outright and return null, and the visible result was
   * that the commonest state in the estate rendered as *"We could not read this academy's
   * payment settings"* — a read failure — instead of *"Payments not set up. Ask a platform
   * administrator to connect Stripe"*. The author was told the site was broken when nothing was
   * wrong and the next step was a sentence away.
   *
   * So the STATE is authoritative and the numbers are conditional on it. They are only ever
   * used behind `pricingEditable`, which demands `verified`; a `verified` tenant whose markup is
   * missing is genuinely contradictory and is still refused, because that is the one case where
   * a zero fee would mean charging a learner less than Stripe costs the academy (R5.4). For
   * every other state the numbers are unreachable, so zeros are safe and the author gets the
   * gate copy that names their actual situation.
   */
  if (state === 'verified' && (currency === null || bps === null || fixed === null || floor === null)) {
    return null;
  }

  return {
    state,
    currency: currency ?? '',
    feeBasisPoints: bps ?? 0,
    feeFixedMinor: fixed ?? 0,
    minimumChargeMinor: floor ?? 0,
    paidEnrollment: raw['paid_enrollment'] === true,
    /**
     * The tenant record's own sentence, passed through untouched (R4.4).
     *
     * The `error` state must render "the reason from the tenant record, verbatim", and the
     * backend emits it as `last_error_message` for exactly that. Paraphrasing it here would be
     * maintaining a second copy of somebody else's error message; inventing one would be worse.
     * Null whenever there is nothing to say, which is every state but `error`.
     */
    lastErrorMessage: typeof raw['last_error_message'] === 'string' && raw['last_error_message']
      ? raw['last_error_message']
      : null,
  };
}

function readCourseRow(raw: unknown, tenant: TenantPayments): [string, PriceView] | null {
  if (!isRecord(raw)) return null;
  const id = raw['course_id'];
  if (typeof id !== 'string' || id === '') return null;

  // BELT AND BRACES (NFR-T1). The server scopes this document by Host and there is no
  // parameter that widens it — the boundary is the server's. This is the same second lock
  // `catalogue.ts:12-27` keeps on a door that is already locked, and it is cheap.
  if (!visibleHere(id)) return null;

  const currency = asCurrency(raw['currency']) ?? tenant.currency;
  const mode = raw['mode'];

  /**
   * THE SERVER'S OWN REFUSAL, AND ITS OWN REASON, TAKE PRECEDENCE (R3.3).
   *
   * The backend emits `unavailable_reason` on course rows as well as pathway rows, and when it
   * does it has ALREADY decided this course cannot be priced — it sets `effective_mode` to
   * `unavailable` and leaves `base_minor`/`fee_minor`/`total_minor` null while keeping `mode`
   * at `paid`. Without this branch such a row fell through to the null-amount check below and
   * was reported with `PRICE_COPY.unknownMode` — *"this price is in a form this version of the
   * site does not understand"* — for a course whose real problem was a closed payment gate or
   * a currency mismatch. The verdict was right and the sentence was wrong, which on an
   * authoring screen sends somebody to debug the wrong thing.
   *
   * Checked BEFORE the `free` branch on purpose: `_course_payload` marks an inactive row
   * unavailable while the row's own mode may still read `free`, and "not on sale" outranks
   * "free" — an author's explicit withdrawal is not a giveaway (R6.0, AC-005).
   */
  const stated = raw['unavailable_reason'];
  if (typeof stated === 'string' && stated !== '') {
    return [id, unknownView(currency, reasonSentence(stated))];
  }

  if (mode === 'free') return [id, freeView(currency)];

  if (mode !== 'paid') {
    // A mode this build does not recognise is NOT a licence to give content away (R6.0,
    // AC-017). Neither is `mode: 'tiered'` from a newer backend.
    return [id, unknownView(currency, PRICE_COPY.unknownMode)];
  }

  if (raw['active'] === false) return [id, unknownView(currency, PRICE_COPY.notOnSale)];

  const total = asPositiveAmount(raw['total_minor']);
  const base = asPositiveAmount(raw['base_minor']);
  const fee = asWholeNumber(raw['fee_minor']);
  if (total === null || base === null || fee === null) {
    // `mode: 'paid'` with a null, NaN, non-integer or non-positive amount. Never FREE and
    // never "RM 0.00" (R6.0, AC-018).
    return [id, unknownView(currency, PRICE_COPY.unknownMode)];
  }

  /*
   * AN UNSYNCED PRICE IS BUYABLE, AND THIS IS THE LINE THAT USED TO DEADLOCK THE FEATURE.
   *
   * It was `buyable: synced`, per AC-003b. The consequence, measured on dev: the payments app
   * DOES self-heal a missing or stale Stripe Price at checkout — `catalog.resolve_for_checkout`
   * repairs it and records `OrderEvent.CATALOG_REPAIRED` — but the repair runs only when a
   * learner reaches checkout, and this line was what stopped them. Nothing could ever get the
   * course out of the unsynced state, so every priced course was permanently unbuyable and the
   * repair path was dead code.
   *
   * Reaching this line already means the SERVER said this price is fine: no
   * `unavailable_reason` (so not `gate_closed`, not `payment_profile_unreadable`, not
   * `currency_mismatch`, not `not_on_sale`, not `amount_invalid`, not `below_minimum_charge`),
   * `mode: 'paid'`, `active`, and three valid amounts. That is precisely the set of states
   * checkout REPAIRS rather than refuses. Every genuine refusal is still refused before the
   * click, by the server, with the server's own sentence.
   *
   * `syncPending` keeps the fact rather than throwing it away: the author's own settings page
   * says the item is not on Stripe yet, which is where that sentence is actionable. The learner
   * sees a price and a working Buy button, because for them there is nothing to act on.
   */
  const synced = raw['price_synced'] === true;
  return [
    id,
    {
      mode: 'paid',
      totalMinor: total,
      baseMinor: base,
      feeMinor: fee,
      // A course has no bundle to be compared against, so there is nothing to strike
      // through: courses carry no discount in this spec (R6.3).
      wasMinor: null,
      savingMinor: null,
      currency,
      buyable: true,
      reason: null,
      syncPending: !synced,
    },
  ];
}

function readPathwayRow(raw: unknown, tenant: TenantPayments): [string, PathwayPriceView] | null {
  if (!isRecord(raw)) return null;
  const key = raw['pathway_key'];
  if (typeof key !== 'string' || key === '') return null;

  const currency = asCurrency(raw['currency']) ?? tenant.currency;
  const rawMode = raw['mode'];
  const mode =
    typeof rawMode === 'string' && PATHWAY_MODES.includes(rawMode) ? (rawMode as PathwayMode) : null;

  const separate = asWholeNumber(raw['separate_minor']);
  const detail = {
    separateBaseMinor: asWholeNumber(raw['separate_base_minor']),
    separateMinor: separate,
    discountBasisPoints: asWholeNumber(raw['discount_basis_points']),
    memberCount: asWholeNumber(raw['member_count']),
    paidMemberCount: asWholeNumber(raw['paid_member_count']),
    wireMode: mode,
  };

  // The server said it could not price this pathway. Its own reason, rendered as a sentence
  // — never a partial sum, never a zero, never the last known value (R3.3, AC-014).
  const stated = raw['unavailable_reason'];
  if (typeof stated === 'string' && stated !== '') {
    return [key, { ...unknownView(currency, reasonSentence(stated)), ...detail }];
  }

  if (mode === null) {
    return [key, { ...unknownView(currency, PRICE_COPY.unknownMode), ...detail }];
  }

  if (mode === 'free') return [key, { ...freeView(currency), ...detail }];

  if (raw['active'] === false) {
    return [key, { ...unknownView(currency, PRICE_COPY.notOnSale), ...detail }];
  }

  const total = asPositiveAmount(raw['total_minor']);
  const base = asPositiveAmount(raw['base_minor']);
  const fee = asWholeNumber(raw['fee_minor']);
  if (total === null || base === null || fee === null) {
    return [key, { ...unknownView(currency, PRICE_COPY.unknownMode), ...detail }];
  }

  // THE SAVING IS THE SERVER'S, and it is only claimed when it is real (R5.5, AC-011). A
  // `fixed` bundle priced above its parts reports `saving_minor: 0`, gets no strike-through
  // and makes no claim — the buy-separately total is still stated in words on the detail
  // page (R6.9), because a bundle that costs more than its parts is a fact the learner is
  // entitled to.
  const saving = asWholeNumber(raw['saving_minor']) ?? 0;
  const realSaving = saving > 0 && separate !== null && separate > total;

  // Same reasoning as the course row above, and the same repair path: a pathway is its own
  // Product at its own bundle Price, so an unsynced bundle is exactly as repairable as an
  // unsynced course. Its member-derived total is also the likeliest thing to go stale, because
  // a member's price changing re-prices the bundle without anybody touching the bundle.
  const synced = raw['price_synced'] === true;
  return [
    key,
    {
      mode: 'paid',
      totalMinor: total,
      baseMinor: base,
      feeMinor: fee,
      wasMinor: realSaving ? separate : null,
      savingMinor: realSaving ? saving : null,
      currency,
      buyable: true,
      reason: null,
      syncPending: !synced,
      ...detail,
    },
  ];
}

/** The server's reason code as a sentence. An unknown code keeps the generic one. */
function reasonSentence(code: string): string {
  if (code === 'membership_unreadable') return PRICE_COPY.membershipUnreadable;
  if (code === 'currency_mismatch') return PRICE_COPY.currencyMismatch;
  if (code === 'not_on_sale') return PRICE_COPY.notOnSale;
  // The remaining three codes `errors.py` defines. All of them mean "the academy cannot take
  // money for this right now", which is not the learner's problem to solve and not a state they
  // can act on — so they share one sentence that says so and does not speculate. A code this
  // build has never heard of lands here too, which is the safe default.
  if (code === 'gate_closed' || code === 'payment_profile_unreadable') {
    return PRICE_COPY.notSetUp;
  }
  if (code === 'amount_invalid') return PRICE_COPY.unknownMode;
  if (code === 'below_minimum_charge') return PRICE_COPY.belowMinimum;
  return `${PRICE_COPY.unavailable}. ${PRICE_COPY.nothingCharged}`;
}

function freeView(currency: string): PriceView {
  return {
    mode: 'free',
    totalMinor: null,
    baseMinor: null,
    feeMinor: null,
    wasMinor: null,
    savingMinor: null,
    currency,
    buyable: true,
    reason: null,
    syncPending: false,
  };
}

function unknownView(currency: string | null, reason: string): PriceView {
  return {
    mode: 'unknown',
    totalMinor: null,
    baseMinor: null,
    feeMinor: null,
    wasMinor: null,
    savingMinor: null,
    currency,
    buyable: false,
    reason,
    syncPending: false,
  };
}

/** The bundle half of a `PathwayPriceView` when there is nothing to say about it. */
const UNKNOWN_DETAIL = {
  separateBaseMinor: null,
  separateMinor: null,
  discountBasisPoints: null,
  memberCount: null,
  paidMemberCount: null,
  wireMode: null,
} as const;

/* ========================================================= the lookups ========= */

/**
 * A course's price, from the seam. THE ONLY WAY a surface asks (R6.0).
 *
 * Three answers, and the reason they are three rather than two is the whole safety property:
 *
 *   the document failed                -> `unknown`. Never free.
 *   the document loaded, course absent -> FREE. Absence within a loaded payload is the
 *                                         payload's own statement that this course is not
 *                                         priced, and an unpriced course is free.
 *   the course is present              -> whatever `readPricingSeam` made of it.
 */
export function coursePriceView(seam: PricingSeam | null | undefined, courseId: string): PriceView {
  if (!seam || !seam.ok) {
    return unknownView(seam?.tenant?.currency ?? null, seam?.reason ?? PRICE_COPY.payloadFailed);
  }
  return seam.courses.get(courseId) ?? freeView(seam.tenant?.currency ?? '');
}

/** The same three answers for a pathway, plus the bundle detail the panels need. */
export function pathwayPriceView(
  seam: PricingSeam | null | undefined,
  pathwayKey: string,
): PathwayPriceView {
  if (!seam || !seam.ok) {
    return {
      ...unknownView(seam?.tenant?.currency ?? null, seam?.reason ?? PRICE_COPY.payloadFailed),
      ...UNKNOWN_DETAIL,
    };
  }
  const found = seam.pathways.get(pathwayKey);
  if (found) return found;
  return { ...freeView(seam.tenant?.currency ?? ''), ...UNKNOWN_DETAIL };
}

/**
 * Is anything on this seam priced at all?
 *
 * For surfaces that must not grow a price element on an academy that sells nothing — the
 * same rule `AdminCourses.tsx:180-190` and `MyLearning.tsx:153-161` already apply to their
 * own controls: a column whose every cell reads "Free" is furniture, not information.
 *
 * A FAILED seam answers `false`, and that is deliberate rather than an oversight: a failure
 * must not conjure a price column onto a page that has never had one. The items on it still
 * resolve `unknown` through `coursePriceView`, so nothing is rendered as free by this.
 */
export function seamHasPrices(seam: PricingSeam | null | undefined): boolean {
  if (!seam || !seam.ok) return false;
  for (const view of seam.courses.values()) if (view.mode !== 'free') return true;
  for (const view of seam.pathways.values()) if (view.mode !== 'free') return true;
  return false;
}

/* ==================================================== authoring preview ======== */

/**
 * The bundle arithmetic of R2.2, for ONE purpose: an author's live preview of a setting they
 * have not saved yet (R6.7's summary).
 *
 * WHY THIS IS NOT A SECOND IMPLEMENTATION OF THE PRICE. Every number a LEARNER sees comes
 * from the server (R7.1), and this function is never called on a learner surface. It exists
 * because the authoring summary has to move as the author types a discount, and asking the
 * server per keystroke is the fan-out R7.1 forbids. The saved numbers replace these the
 * moment the write returns, and where the two disagree the server's win (R2.5).
 *
 * INTEGER ONLY, and both rounding directions are the spec's:
 *   - the DISCOUNT is floored, so it can never round up past what the author asked for;
 *   - the MARKUP is ceiled, so the academy never absorbs a fraction of Stripe's fee.
 *
 * The markup carries BOTH legs. A percentage alone under-collects Stripe's `+ RM1.00` on
 * every sale — on a RM20 course a 4% markup collects RM0.80 against a true fee of RM1.60
 * (payments foundation §5.1).
 */
export interface PathwayTotals {
  /** Σ member bases. What a percentage discount applies to. */
  separateBaseMinor: number;
  /** The bundle's own base, before the markup. The academy's net. */
  bundleMinor: number;
  feeMinor: number;
  /** What the learner is charged. Equals the synced Stripe Price's `unit_amount` (D3). */
  totalMinor: number;
  /** Buying the members one at a time: N charges, therefore N fixed markup legs. */
  separateMinor: number;
  savingMinor: number;
}

export interface PathwayPreviewInput {
  mode: PathwayMode;
  /** Each PAID, ACTIVE member's `base_minor` in this tenant. Free members are omitted. */
  memberBaseMinor: number[];
  /** Required for `fixed`. */
  amountMinor?: number | null;
  /** Required for `percent_discount`. Basis points: 15% is 1500. */
  discountBasisPoints?: number | null;
  fee: Pick<TenantPayments, 'feeBasisPoints' | 'feeFixedMinor' | 'minimumChargeMinor'>;
}

export function previewPathwayTotals(input: PathwayPreviewInput): PathwayTotals {
  const bases = input.memberBaseMinor.filter((amount) => Number.isInteger(amount) && amount > 0);
  const separateBaseMinor = bases.reduce((sum, amount) => sum + amount, 0);

  let bundleMinor: number;
  switch (input.mode) {
    case 'free':
      bundleMinor = 0;
      break;
    case 'sum':
      bundleMinor = separateBaseMinor;
      break;
    case 'fixed':
      bundleMinor = Math.max(0, Math.trunc(input.amountMinor ?? 0));
      break;
    case 'percent_discount': {
      const bps = clampBasisPoints(input.discountBasisPoints);
      bundleMinor = separateBaseMinor - Math.floor((separateBaseMinor * bps) / 10_000);
      break;
    }
  }

  // A free bundle is free, and a free bundle carries no processing markup: there is nothing
  // to process. `total_minor` is 0 and `priceLabel` renders the word "Free", not a zero.
  const feeMinor = bundleMinor === 0 ? 0 : markup(bundleMinor, input.fee);
  const totalMinor =
    bundleMinor === 0 ? 0 : Math.max(input.fee.minimumChargeMinor, bundleMinor + feeMinor);

  // EACH MEMBER MARKED UP AS THE SEPARATE PURCHASE IT WOULD BE (R2.2). Both sides of the
  // comparison are post-markup, so both are amounts a learner could really be charged —
  // which is what makes the saving honest rather than understated. The bundle is one Stripe
  // Product at one Price (D2), so it carries the fixed leg once where the parts carry it N
  // times, and the difference is real money.
  const separateMinor = bases.reduce(
    (sum, amount) =>
      sum + Math.max(input.fee.minimumChargeMinor, amount + markup(amount, input.fee)),
    0,
  );

  return {
    separateBaseMinor,
    bundleMinor,
    feeMinor,
    totalMinor,
    separateMinor,
    savingMinor: Math.max(0, separateMinor - totalMinor),
  };
}

/** `ceil(base × bps / 10000) + fixed` — both legs, always (R5.4, foundation §5.2). */
export function markup(
  baseMinor: number,
  fee: Pick<TenantPayments, 'feeBasisPoints' | 'feeFixedMinor'>,
): number {
  return Math.ceil((baseMinor * fee.feeBasisPoints) / 10_000) + fee.feeFixedMinor;
}

/** 0..10000. A discount outside that range is not one the author can have meant. */
function clampBasisPoints(value: number | null | undefined): number {
  if (value === null || value === undefined || !Number.isInteger(value)) return 0;
  return Math.min(10_000, Math.max(0, value));
}

/* ============================================================== writes ========= */

/**
 * Set a course's price (R7.2). ITS OWN MUTATION, and that is the whole point.
 *
 * It must NOT join `CourseSettings.tsx`'s draft: that form saves by WHOLE-OBJECT PUT to
 * Studio's own endpoint, which reads only the fields it knows and treats an absent date key
 * as "clear that date" (`CourseSettings.tsx:12-15`). A price added to that payload would be
 * sent to Studio and silently dropped, and the form would report a save that never happened
 * (spec Assumption 7).
 *
 * `amount_minor` is the AUTHOR's net. The markup is added server-side and the author is
 * shown what the learner pays (R6.1) — an author who does not know what the learner pays
 * cannot price anything.
 */
export interface CoursePriceWrite {
  mode: CourseMode;
  /** Minor units. Null when free. */
  amount_minor: number | null;
}

export const saveCoursePrice = (courseId: string, body: CoursePriceWrite) =>
  openedx.put<WireCoursePrice>(`${PRICING_API}/courses/${encodeURIComponent(courseId)}`, body);

/**
 * WHAT THE BUY BUTTON DOES — the one call, and the edge of this spec.
 *
 * `thinlms-course-pathway-pricing_spec.md` defines the six buy states and stops: "it does not
 * define what `buy` does", and "Everything after the click belongs to
 * `thinlms-payments-foundation_spec.md`". So this function is deliberately thin — it is the
 * seam, not the mechanism. It asks the payments backend for a Checkout Session and hands the
 * learner to the URL it returns.
 *
 * THE BROWSER SENDS NO AMOUNT, and that is the important part. The number this app displayed
 * is not the number charged: sibling F charges against the Stripe Price it synced and refuses
 * rather than charging when its own quote disagrees with it (R2.5, foundation §4.6). A price
 * the browser sent would be a price a browser could change.
 *
 * NO CODE FIELD AND NO `discounts` (D4, coupons FR-5.1). The Session carries
 * `allow_promotion_codes` and the learner types a code into Stripe's own field on Stripe's own
 * page. thinlms neither collects nor validates one.
 *
 * Until sibling F ships this endpoint it 404s, which surfaces as "Pricing is not available on
 * this site yet." through `explainPricingFailure` — a stated refusal before any money moves,
 * which is the same posture as every other unbuyable state.
 */
export interface CheckoutTarget {
  kind: 'course' | 'pathway';
  /** The course key or the pathway key. The server resolves the price; we name the thing. */
  key: string;
  /** Where to come back to. Same-origin path only — the server rejects anything else. */
  returnPath: string;
}

export interface CheckoutSession {
  /** Stripe's hosted page. The learner is sent here; nothing is charged on our origin. */
  checkout_url: string;
}

export const beginCheckout = (target: CheckoutTarget) =>
  openedx.post<CheckoutSession>(`${PAYMENTS_API}/checkout`, target);

/* ============================================== the order after the payment ===== */

/**
 * WHAT HAPPENED TO ONE PURCHASE — the read behind `/purchase/complete`.
 *
 * `checkout.py` sends Stripe a `success_url` of `/purchase/complete?order={order_id}` and
 * `/purchase/cancelled?order={order_id}` for the cancel case. Neither route existed and neither
 * did this endpoint, so every completed payment landed on a page that was not there.
 *
 * `outcome` IS THE SERVER'S OWN COLLAPSE OF `payment_state`, and it is read as the authority
 * rather than re-derived from `payment_state` here. Twelve payment states map onto five
 * renderings, the mapping is a judgement about money, and a second implementation of it in the
 * browser is exactly the drift the rest of this file exists to avoid. `payment_state` and
 * `access_state` still travel, for a support conversation and a HAR file.
 *
 * `access_granted` IS NOT DERIVED FROM `outcome`, and the pair that matters is
 * `paid` + `access_granted: false`: the money is taken and the enrolment has not been written
 * yet. That is a NORMAL state for a few seconds while the fulfilment outbox runs, and it must
 * not render as a failure — telling somebody their payment failed when it succeeded is the
 * worst sentence on this page.
 */
export type OrderOutcome =
  | 'awaiting_payment'
  | 'paid'
  | 'no_charge'
  | 'not_paid'
  | 'refunded'
  | 'disputed';

export interface OrderStatus {
  order_id: string;
  outcome: OrderOutcome;
  /** The raw state, for support. Never branched on in place of `outcome`. */
  payment_state: string;
  access_state: string;
  access_granted: boolean;
  /** Access was deliberately taken back — a settled outcome, never "still coming". */
  access_revoked: boolean;
  item_kind: 'course' | 'pathway';
  item_key: string;
  /** THE QUOTE — what the learner was asked for. Not necessarily what they were charged. */
  amount_minor: number;
  fee_minor: number;
  /**
   * WHAT THEY WERE ACTUALLY CHARGED. NULL until something settles, and null is not zero.
   *
   * Added because the receipt could not be right without it: a discounted purchase printed the
   * quote as the amount paid, overstating a learner's own payment by the discount they earned.
   */
  charged_minor: number | null;
  /** What Stripe took off, as Stripe reported it. Null when there was no discount. */
  discount_minor: number | null;
  currency: string;
  /** The currency the CHARGE settled in, which Adaptive Pricing can make different. */
  charge_currency: string;
  paid_at: string | null;
  fulfilled_at: string | null;
  created_at: string;
}

/**
 * One order, for the learner who placed it.
 *
 * The server scopes it to `request.user` AND to the academy the request is on, and answers 404
 * for anything else — including another learner's real order id, which is why a miss here must
 * be rendered as "we cannot find this order" and never as a failed payment. An order id travels
 * through Stripe into a URL and a browser history; a 403 would confirm that a pasted id is real.
 */
export const loadOrderStatus = (orderId: string) =>
  openedx.get<OrderStatus>(`${PAYMENTS_API}/orders/${encodeURIComponent(orderId)}`);

/** The React Query key for one order. Namespaced so nothing else can collide with it. */
export const orderStatusKey = (orderId: string) => ['thinlms-order', orderId] as const;

export interface PathwayPriceWrite {
  mode: PathwayMode;
  amount_minor?: number | null;
  discount_basis_points?: number | null;
}

export const savePathwayPrice = (pathwayKey: string, body: PathwayPriceWrite) =>
  openedx.put<WirePathwayPrice>(`${PRICING_API}/pathways/${encodeURIComponent(pathwayKey)}`, body);

/**
 * The ADMIN read (R7.2): inactive rows included, bundle detail included.
 *
 * A separate document from the learner one because it answers a different question — an
 * author has to see the price of a course that is not on sale, and a learner must not.
 *
 * BOUNDED THE SAME WAY, for the same reason: an admin course list at 1000 courses pages at
 * 24 rows, so it asks about 24 prices. Same never-rejects contract, same 404 rule.
 */
export const adminPricesQueryKey = (kind: 'courses' | 'pathways', keys: readonly string[]) =>
  ['pricing', 'admin', kind, [...keys].sort().join(KEY_DELIMITER)] as const;

export async function loadAdminPrices(request: PriceRequest): Promise<PricingSeam> {
  const courses = dedupe(request.courseIds);
  const pathways = dedupe(request.pathwayKeys);
  if (courses.length === 0 && pathways.length === 0) return unpricedSeam(null);

  const seams = await Promise.all(planBatches(courses, pathways).map(readAdminBatch));
  return mergeSeams(seams);
}

async function readAdminBatch(request: PriceRequest): Promise<PricingSeam> {
  const params = new URLSearchParams();
  for (const id of request.courseIds ?? []) params.append('course', id);
  for (const key of request.pathwayKeys ?? []) params.append('pathway', key);

  let body: unknown;
  try {
    // `admin/catalogue`, not `admin/prices`: see the route note at the top of this file.
    body = await openedx.get<unknown>(`${PRICING_API}/admin/catalogue?${params}`, true);
  } catch (error) {
    if (error instanceof ApiError && error.status === 404 && !tenantSellsAnything()) {
      return unpricedSeam(null);
    }
    reportPayloadRejected(
      error instanceof ApiError ? `admin prices: HTTP ${error.status}` : 'admin prices failed',
    );
    return failedSeam(PRICE_COPY.payloadFailed);
  }
  if (body === null || body === undefined) {
    if (!tenantSellsAnything()) return unpricedSeam(null);
    return failedSeam(PRICE_COPY.payloadFailed);
  }
  return readPricingSeam(body);
}

/**
 * A refusal, in the server's own words wherever it gave any.
 *
 * `AdminPaths.tsx:17-21` states the requirement: "Every refusal is shown with the server's
 * own sentence … none of that survives being replaced with 'something went wrong'." A 409
 * here names the payment state and a 400 names the minimum charge, and both are the only
 * thing that tells an author what to do next.
 */
export function explainPricingFailure(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return 'The academy could not be reached. Nothing has been saved.';
  }

  const body = error.body as { detail?: unknown; message?: unknown } | null;
  const detail =
    typeof body?.detail === 'string'
      ? body.detail
      : typeof body?.message === 'string'
        ? body.message
        : null;
  if (detail) return detail;

  if (error.status === 403) {
    return 'This belongs to another academy, so its price cannot be changed here.';
  }
  if (error.status === 409) return 'Pricing is not unlocked for this academy yet.';
  if (error.status === 404) return 'Pricing is not available on this site yet.';
  return `The academy answered ${error.status}. Nothing has been saved.`;
}

/* ============================================================== helpers ======== */

/**
 * `thinlms.pricing.payload_rejected` (spec Observability). Without this line AC-016 passes
 * in production in silence: "the backend is down" and "the backend changed shape" look
 * identical from a page full of "Price unavailable".
 */
function reportPayloadRejected(what: string): void {
  console.error(
    `[pricing] payload rejected: ${what}. Every price on this page reads as unavailable.`,
  );
}

function visibleHere(courseId: string): boolean {
  try {
    return courseIdVisibleToTenant(courseId);
  } catch {
    // No config loaded — a render before boot, or a test. The server already scoped this
    // document, so the belt is missing here and the braces still hold.
    return true;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

/** An integer, or null. `NaN`, `"12500"`, `12.5` and `null` are all null. */
function asWholeNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) ? value : null;
}

/** An integer strictly greater than zero, or null. A paid price of 0 is not a price. */
function asPositiveAmount(value: unknown): number | null {
  const whole = asWholeNumber(value);
  return whole !== null && whole > 0 ? whole : null;
}

/** Three letters, upper-cased, or null. `Intl` throws on anything else. */
function asCurrency(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const code = value.trim().toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : null;
}
